import React, {createContext, useContext} from "react";

/**
 * Carries the Phase-2 background-transform selector (issue #528, 2a) from App
 * down to the ~60 <BgTransformer> instances scattered across the page tree,
 * without threading a prop through every page component.
 *
 * This is NOT shadow state: it holds no fetched data. `getEffectiveTransform`
 * is a pure selector closing over React Query caches (the catalog + resolved
 * scene) and the images context's user-intent slice — the single source of
 * truth per the no-shadow-state rule. When `enabled` is false (bgDerivation
 * flag off) the selector always returns null and consumers keep their legacy
 * context-driven behavior, so the default value below is a safe no-op.
 */
export type EffectiveTransform = {
  x: number;
  y: number;
  scale: number;
  rotate: number;
  flip: {x: boolean; y: boolean};
};

export type BgTransformContextValue = {
  enabled: boolean;
  /** Gallery previews can run while a saved scene keeps its own canvas crop. */
  previewsEnabled: boolean;
  /**
   * `background` is the option the calling template is displaying — required
   * from BgTransformer (whose nested ALL-products provider carries the child
   * carousel's option); omitted by buildScene composition, which lets the
   * selector fall back to the carousel state (see effectiveBgOptionKey).
   */
  getEffectiveTransform: (
    templateId: string,
    background?: string | number
  ) => EffectiveTransform | null;
  /**
   * True when `getEffectiveTransform` reads loaded layers for the layout and
   * background on screen. Before that it yields no crop, and a scene built
   * then carries none.
   */
  transformsReady: boolean;
  /**
   * The crop `templateId` gets when the background selector's tile `variation`
   * is picked. Undefined while the tile layers load or where tiles show no
   * preview; null means no crop (natural size, top-left).
   */
  getTileTransform: (
    templateId: string,
    variation: string | number
  ) => EffectiveTransform | null | undefined;
  /** Per-banner preset URL; null means catalog image, undefined means loading. */
  getTileImage: (templateId: string, variation: string | number) => string | null | undefined;
  /**
   * The banner the saved default scene of tile `variation` names for its
   * crop preview (`bgPreviewTemplate`), or null. Undefined while loading.
   */
  getTileSavedPreviewTemplate: (variation: string | number) => string | null | undefined;
  /**
   * The banners whose image tile `variation` takes from a row of the current
   * layout (the layout's own artwork). Undefined while loading.
   */
  getTileLayoutImages: (variation: string | number) => string[] | undefined;
  /**
   * The base colour (hex, no "#") `templateId` gets when tile `variation` is
   * picked, from that background's default scene. Undefined while loading.
   */
  getTileBase: (templateId: string, variation: string | number) => string | undefined;
  /**
   * True while the route's default scene is not applied yet on a channel that
   * bakes backgrounds (libs/bakedBg.ts). A banner then shows no image: the
   * default decides between a baked image and a positioned one, and showing
   * the gallery image first loads a full-size picture and moves it twice.
   */
  awaitingDefaultScene: boolean;
};

const DEFAULT_VALUE: BgTransformContextValue = {
  enabled: false,
  previewsEnabled: false,
  getEffectiveTransform: () => null,
  transformsReady: true,
  getTileTransform: () => undefined,
  getTileImage: () => undefined,
  getTileSavedPreviewTemplate: () => undefined,
  getTileLayoutImages: () => undefined,
  getTileBase: () => undefined,
  awaitingDefaultScene: false,
};

export const BgTransformContext = createContext<BgTransformContextValue>(DEFAULT_VALUE);

export function useBgTransformContext(): BgTransformContextValue {
  return useContext(BgTransformContext);
}
